<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import Table from '../Table.svelte';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import { options } from '../../configs/BarOptions';
  import DateRange from '../common/DateRange.svelte';
  import { colors, resetChart } from '../../utils/common';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getSwapFeesAnalytics } from '../../../#services/getSwapAnalytics';

  let chart, table;

  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let token = 'WLIS';

  let ligamentsPercents;

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2023-04-10'
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refresh() {
    resetChart(chart, labels, datasets);

    const requestData = {
      baseCurrency: token,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    };

    getSwapFeesAnalytics.send({ data: requestData });
  }

  getSwapFeesAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      value.value.data.forEach(e => {
        labels.push(e.date);
      });

      const ligamentsDatasets = {
        'WLIS/USDT': [],
        'WLIS/NEAR': [],
        'NEAR/USDT': [],
        'MATIC/USDT': [],
        'WLIS/MATIC': [],
      };

      for (const data of value.value.data) {
        for (const ligament of data.ligaments) {
          ligamentsDatasets[ligament.ligament].push({ amount: ligament.total, amountForChart: ligament.totalToday, date: data.date, rawDate: data.rawDate });
        }
      }

      const length = value.value.data.length;
      const ligamentsSum = value.value.data[length - 1].ligaments.reduce((a, b) => a + b.total, 0);

      ligamentsPercents = [
        (value.value.data[length - 1].ligaments[0].total * 100 / ligamentsSum).toFixed(2) + ' %',
        (value.value.data[length - 1].ligaments[1].total * 100 / ligamentsSum).toFixed(2) + ' %',
        (value.value.data[length - 1].ligaments[2].total * 100 / ligamentsSum).toFixed(2) + ' %',
        (value.value.data[length - 1].ligaments[3].total * 100 / ligamentsSum).toFixed(2) + ' %',
        (value.value.data[length - 1].ligaments[4].total * 100 / ligamentsSum).toFixed(2) + ' %',
      ];

      let idx = 0;
      for (const dataset of Object.keys(ligamentsDatasets)) {
        datasets.push({
          label: `${dataset}, ${token}`,
          backgroundColor: colors[idx],
          borderColor: colors[idx++],
          fill: false,
          stack: 0,
          data: ligamentsDatasets[dataset].map((d) => d.amountForChart),
        });
      }

      try {
        table = Object.fromEntries(Object.entries(ligamentsDatasets).map(([key, value]) => {
          return [`💯 ${key}, ${token}`, value];
        }).concat([[`💯 Total Fee, ${token}`, value.value.data.map((d) => ({ amount: d.ligaments.reduce((a, b) => a + b.total, 0), date: d.date }))]]));
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <span class="date-label">Base currency</span>
    <select class="form-select" bind:value={token} on:change={refresh}>
      <option value="WLIS" selected>LIS</option>
      <option value="USDT">USDT</option>
      <option value="NEAR">NEAR</option>
      <option value="MATIC">MATIC</option>
    </select>
  </div>
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">Swaps Fees Analytics.</p>
<div class="graphic">
  {#if $getSwapFeesAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getSwapFeesAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getSwapFeesAnalytics.value}
    <Chart bind:this={chart} type="bar" {data} width={500} height={270} {options} />
    <Table branches={table} isTableHidden={false} hideTotal={true} additional={{'%': [ligamentsPercents[0], ligamentsPercents[1], ligamentsPercents[2], ligamentsPercents[3], ligamentsPercents[4], '100%']}} />
  {/if}
</div>